import * as React from "react";
import { connect, useDispatch, useSelector } from "react-redux";
import { Button, Box, Typography, FormControlLabel, Checkbox, Tooltip } from "@mui/material";
import { push } from "connected-react-router";
import { useForm } from "react-hook-form";
import { omitBy, isNil } from "lodash";
import InfoIcon from "@mui/icons-material/Info";
import { useLocation } from "react-router";
import * as clientActions from "../../actions/client";
import StatefulButton from "../../components/StatefulButton/StatefulButton";
import { tl } from "../../components/translate";
import Panel from "../../components/Panel";
import Can from "../Policy/Can";
import { IThunkDispatch, RootState } from "../../store";
import { Client } from "../../interfaces/ClientInterface";
import { CustomerType } from "../../interfaces/Accounts";
import Form from "./Form";
import { ledgerFindBySubLedgerType } from "../accounts/hooks";
import { notificationAdd } from "../../actions/notification";
import useCurrentResourceCentre from "../../hooks/useCurrentResourceCentre";
import { isValidMobileNumber } from "../../helpers/validators";
import { getAgeFromDOB } from "../../helpers/formatters";
import { ServiceProvider } from "../../interfaces/ServiceProvidersInterface";
import useHasLeadClientEnabled from "../../hooks/useHasLeadClientEnabled";

export const enum Mode {
  EDIT = "edit",
  CREATE = "create"
}

interface Props {
  afterSave: () => void;
  clientDefaultName: string;
  client: Client;
  onSave: (
    data: Partial<Client>,
    stayOnCurrentPage: boolean,
    searchQuery?: string
  ) => Promise<Client>;
  onCancel: () => void;
  showFooter: boolean;
  mode: string;
  navigateTo: (v: string) => void;
  setEditMode: () => void;
  stayOnCurrentPage: boolean;
  wrapperStyle: {
    [key: string]: string;
  };
  resourceCentreId?: number;
  fromBill?: boolean;
  isCorporateClient?: boolean;
  isForClientRegistration?: boolean;
  isForLeadClientBilling?: boolean;
  saveButtonText?: React.ReactNode;
  onSpChange?: (sp: ServiceProvider) => void;
}

const initialFormState = {
  fullName: "",
  phone: "",
  secondaryPhone: "",
  email: "",
  gender: undefined,
  dob: "",
  address: "",
  city: "",
  occupation: "",
  insuranceNumber: "",
  bloodGroup: "",
  maritalStatus: "",
  ethnicity: "",
  palikaId: undefined,
  nationality: "",
  appliedCountry: "",
  passportInfo: {
    passportNumber: "",
    issueDate: "",
    expiryDate: "",
    issuingAuthority: ""
  },
  insuranceImage: undefined,
  image: undefined,
  ipdNo: "",
  wardNo: "",
  registrationNo: "",
  transactionType: undefined,
  openingBalance: 0,
  associatedCompanyId: undefined,
  nationalIdentityNumber: "",
  citizenshipNumber: "",
  panVatNumber: "",
  guardians: []
};

const initialCorporateFormState = {
  fullName: "",
  phone: "",
  secondaryPhone: "",
  email: "",
  address: "",
  city: "",
  openingBalance: 0,
  paymentTerm: undefined,
  panVatNumber: "",
  transactionType: undefined,
  insuranceImage: undefined,
  guardians: []
};

const ClientCreateEdit = ({
  afterSave,
  clientDefaultName,
  client,
  onSave,
  onCancel,
  showFooter = true,
  mode,
  navigateTo,
  setEditMode,
  stayOnCurrentPage,
  wrapperStyle,
  resourceCentreId = undefined,
  fromBill = false,
  isCorporateClient = false,
  saveButtonText = tl("client.save"),
  isForClientRegistration,
  onSpChange,
  isForLeadClientBilling = false
}: Props) => {
  // hooks
  const location = useLocation();
  const dispatch = useDispatch();
  const currentResourceCentre = useCurrentResourceCentre();
  const { isLeadClientEnabled } = useHasLeadClientEnabled();

  // selectors
  const isAccountSubscribed = useSelector(
    (state: RootState) => state.subscriptions.currentSubscription?.features?.account?.subscribed
  );

  // states
  const [saving, setSaving] = React.useState(false);
  const [isLeadClient, setIsLeadClient] = React.useState(
    isLeadClientEnabled && isForLeadClientBilling
  );
  const [clientWithSamePhoneAndName, setClientWithSamePhoneAndName] = React.useState(null);

  // form
  const {
    handleSubmit,
    control,
    watch,
    formState: { errors },
    setError,
    clearErrors
  } = useForm<Client>({
    defaultValues:
      mode === Mode.CREATE
        ? {
            ...(isCorporateClient ? initialFormState : initialCorporateFormState),
            fullName: clientDefaultName || ""
          }
        : {
            ...client,
            guardians: client?.guardians || [],
            isLead: false,
            fullName: `${client?.firstName || ""} ${client?.lastName || ""}`
          }
  });

  const formState = watch();

  // helper function
  let hasValidPhoneAndAge = isValidMobileNumber({ msg: "" })(formState.phone);
  const isSendAppLinkToClientEnabled =
    currentResourceCentre?.settings?.clientSettings?.sendAppLinkToClient || false;

  const [sendAppLinkToClient, setSendAppLinkToClient] = React.useState(
    isSendAppLinkToClientEnabled && mode === Mode.CREATE && hasValidPhoneAndAge
  );
  if (formState.dob) {
    hasValidPhoneAndAge = hasValidPhoneAndAge && getAgeFromDOB(formState.dob) > 15;
  }

  const clientLedger = ledgerFindBySubLedgerType(CustomerType.CLIENT);

  const handleFormSubmit = async (submittedData) => {
    if (clientWithSamePhoneAndName) {
      dispatch(
        notificationAdd({
          id: new Date().getUTCMilliseconds(),
          variant: "error",
          message:
            "Client with same name and phone number found. Please change name or phone number before continuing.",
          autoTimeout: true
        })
      );
      return;
    }

    setSaving(true);
    const names = submittedData.fullName.trim().split(" ");
    // fullName is set as firstName for corporate client on db
    const formData = omitBy(
      {
        ...submittedData,
        sendAppLinkToClient,
        ...(resourceCentreId && { resourceCentreId }),
        ...(isCorporateClient && {
          firstName: submittedData.fullName,
          lastName: "",
          paymentTerm: Number(submittedData.paymentTerm),
          isCorporateClient: true
        }),
        ...(!isCorporateClient && {
          gender: parseInt(submittedData.gender, 10),
          lastName: names.length > 1 ? names.pop() : "",
          firstName: names.join(" "),
          isLead: isLeadClient
        }),
        ...(submittedData.openingBalance !== null && submittedData.openingBalance !== undefined
          ? { openingBalance: Number(submittedData.openingBalance) }
          : {})
      },
      (v) => isNil(v) || v === ""
    );

    if (mode === "create" && isAccountSubscribed && clientLedger) {
      formData.ledgerId = clientLedger.id;
    }

    if (sendAppLinkToClient && currentResourceCentre.subscriptions.features.sms.volumeLeft <= 0) {
      dispatch(
        notificationAdd({
          id: new Date().getUTCMilliseconds(),
          variant: "warning",
          message:
            "You don’t have an active SMS subscription or enough credits to send this message. Client will be created but SMS won't be sent to them",
          autoTimeout: true
        })
      );
    }

    onSave(formData, stayOnCurrentPage, location.search).then(() => {
      setSaving(false);
      if (afterSave) {
        afterSave();
      }
      if (setEditMode) {
        setEditMode(false);
      }
    });
  };

  // effects
  React.useEffect(() => {
    if (isSendAppLinkToClientEnabled) {
      setSendAppLinkToClient(hasValidPhoneAndAge);
    }
  }, [hasValidPhoneAndAge]);

  return (
    <Can
      policyAccessKey={["clients:createClient", "clients:editClient"]}
      superAdminPass
      supportAccountAdminPass
      partialCheck
    >
      <Panel
        wrapperStyle={wrapperStyle}
        onClose={onCancel}
        title={mode === "edit" ? tl("client.updateClient") : tl("client.createNewClient")}
        footer={
          showFooter && (
            <Box display="flex" justifyContent="space-between" width="100%">
              <Box display="flex" alignItems="center" gap={1}>
                {isSendAppLinkToClientEnabled && mode === Mode.CREATE && (
                  <Box display="flex" alignItems="center" gap={0.5}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          disabled={!hasValidPhoneAndAge}
                          checked={sendAppLinkToClient}
                          onChange={() => setSendAppLinkToClient(!sendAppLinkToClient)}
                        />
                      }
                      label="Send app link to client"
                    />
                    <Tooltip
                      title="Send Okhati patient app link to client. No sms credit needed. Sms will be sent if patient is above 16."
                      arrow
                    >
                      <InfoIcon color="info" />
                    </Tooltip>
                  </Box>
                )}
                {isLeadClientEnabled && mode === Mode.CREATE && (
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={isLeadClient}
                        onChange={(_, checked) => setIsLeadClient(checked)}
                      />
                    }
                    label="Lead Client"
                  />
                )}
              </Box>
              <Box display="flex" justifyContent="space-between" alignItems="center">
                <Button
                  data-testmation="client.cancel"
                  onClick={() => {
                    if (mode === "edit") {
                      onCancel();
                    } else {
                      navigateTo(`/clients${location.search}`);
                    }
                    if (setEditMode) {
                      setEditMode(false);
                    }
                  }}
                  style={{ marginRight: "32px" }}
                >
                  <Typography>{tl("client.cancel")}</Typography>
                </Button>
                <StatefulButton
                  data-testmation="clientSave"
                  variant="contained"
                  color="primary"
                  disabled={saving}
                  onClick={handleSubmit(handleFormSubmit)}
                  isLoading={saving}
                  circularProgressProps={{ size: 16 }}
                >
                  <Typography>{saveButtonText}</Typography>
                </StatefulButton>
              </Box>
            </Box>
          )
        }
      >
        <Box sx={{ width: "100%" }}>
          <Form
            isForClientRegistration={isForClientRegistration}
            onSpChange={onSpChange}
            isCorporateClient={isCorporateClient}
            fromBill={fromBill}
            clearErrors={clearErrors}
            setError={setError}
            client={client}
            mode={mode}
            formState={formState}
            control={control}
            errors={errors}
            sameClient={clientWithSamePhoneAndName}
            setSameClient={setClientWithSamePhoneAndName}
          />
        </Box>
      </Panel>
    </Can>
  );
};

export default connect(
  (state: RootState, { client, clientId }: { client: Client; clientId: number }) => ({
    client: client || state.clients.collection?.find(({ id }) => Number(clientId) === id)
  }),
  (dispatch: IThunkDispatch) => ({
    onSave: async (clientData, stayOnCurrentPage, searchQuery) => {
      await dispatch(clientActions.saveClient(clientData, stayOnCurrentPage, searchQuery));
    },
    navigateTo: (url) => dispatch(push(url))
  })
)(ClientCreateEdit);
